import {
  CommonAccessType,
  EntryPermissionAccessRule,
  EntrySettings,
  ListAccessType
} from '@/api/types/entry-types';
import {
  accessRuleTypeToAccessTypeMap,
  AccessType
} from '@/utils/privacy/types/AccessType';
import {
  communityAllEntryPrivacySettings,
  entryPrivacySettingsTypes
} from '@/configs/dybr';

export interface AccessItem {
  type?: string;
  id?: string;
  deny?: boolean;
}

export interface InternalAccessSettings {
  accessType: CommonAccessType | ListAccessType | null | undefined;
  denyCustomList: string[];
  allowCustomList: string[];
}

export interface SelectedAccess {
  type?: string;
}

export interface ListOption {
  value: string;
}

export interface CurrentAccessType {
  accessSettingType: AccessType | null | undefined;
  lists?: string[];
}

/**
 * Maps entry permission settings to a simplified structure where accessType
 * is the last access rule type (the entry specific one) and custom lists are
 * separated into allow and deny lists. This makes it easier to work with
 * access settings in the UI.
 *
 * @param readAccess list of access rules from entry settings
 * @returns calculated summarized access level
 */
export function getInternalAccessSettings(
  readAccess?: EntryPermissionAccessRule[]
): InternalAccessSettings {
  const internalSettings: InternalAccessSettings = {
    accessType: undefined,
    denyCustomList: [],
    allowCustomList: []
  };
  if (!readAccess?.length) {
    return internalSettings;
  }
  for (const access of readAccess) {
    if (!access?.type) {
      internalSettings.accessType = null;
      continue;
    }
    if (access.type === 'list') {
      access.deny
        ? internalSettings.denyCustomList.push(access.id)
        : internalSettings.allowCustomList.push(access.id);
    } else {
      internalSettings.accessType = access.type;
    }
  }
  return internalSettings;
}

export function getCurrentAccessType(
  settings?: EntrySettings
): CurrentAccessType {
  const readAccess = settings?.permissions?.access;
  const internalSettings = getInternalAccessSettings(readAccess);

  if (internalSettings.denyCustomList?.length) {
    return {
      accessSettingType: AccessType.denyCustomList,
      lists: internalSettings.denyCustomList
    };
  } else if (
    internalSettings.allowCustomList?.length &&
    internalSettings.accessType === AccessType.private
  ) {
    return {
      accessSettingType: AccessType.allowCustomList,
      lists: internalSettings.allowCustomList
    };
  }
  return {
    accessSettingType:
      accessRuleTypeToAccessTypeMap[
        internalSettings.accessType as CommonAccessType
      ]
  };
}

export function calculateReadAccessSettings(
  selectedAccess?: SelectedAccess | null,
  settings?: EntrySettings
): AccessItem[] {
  const internalSettings = getInternalAccessSettings(
    settings?.permissions?.access
  );
  switch (selectedAccess?.type) {
    case AccessType.allowCustomList:
      return [
        { type: AccessType.private },
        ...(internalSettings.allowCustomList?.map(id => ({
          type: 'list',
          id
        })) ?? [])
      ];
    case AccessType.denyCustomList:
      return [
        { type: AccessType.registered },
        ...(internalSettings.denyCustomList?.map(id => ({
          type: 'list',
          id,
          deny: true
        })) ?? [])
      ];
    default:
      return selectedAccess ? [selectedAccess] : [];
  }
}

export function getCustomListReadAccess(
  accessType: string,
  options: ListOption[]
): AccessItem[] {
  return accessType === AccessType.allowCustomList
    ? [
        { type: AccessType.private },
        ...options.map(option => ({
          type: 'list',
          id: option.value // `option.value` is an ID of the custom list
        }))
      ]
    : [
        { type: AccessType.registered },
        ...options.map(option => ({
          type: 'list',
          id: option.value, // `option.value` is an ID of the custom list
          deny: true
        }))
      ];
}

interface PrivacySettingType {
  label: string;
  value: { type: string } | null;
}

export interface AccessLevelData {
  label: string;
  customListIds: string[];
}

/**
 * Determines whether the lock icon should be hidden based on access type and edit permissions
 */
export const shouldHideLockIcon = (
  accessSettingType: string | null | undefined,
  canEdit: boolean
): boolean =>
  // Don't show a lock if the entry is closed for guests only
  ['registered'].includes(accessSettingType as string) ||
  // Don't show a lock if the entry is deny-list and user is not the author
  (['deny-custom-list'].includes(accessSettingType as string) && !canEdit);

/**
 * Gets access level label for displaying in the lock icon popover
 */
export function getAccessLevelLabel(
  accessSettingType: string | null | undefined,
  isCommunity: boolean
): string | null {
  const permissionTypes: PrivacySettingType[] = isCommunity
    ? communityAllEntryPrivacySettings
    : entryPrivacySettingsTypes;

  const label = permissionTypes.find(
    type => type.value && accessSettingType === type.value.type
  )?.label;

  return label || null;
}
